import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { createRouter, RouterProvider } from "@tanstack/react-router";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { PagePending } from "./routes/__root";
import { routeTree } from "./routeTree.gen";
import "./styles.css";

// A tab opened before a deploy may lazy-load a chunk that no longer exists; reload once to pick
// up the new build instead of showing a MIME-type error.
window.addEventListener("vite:preloadError", (event) => {
  event.preventDefault();
  const key = "auradata:reloaded-for-chunk";
  if (sessionStorage.getItem(key) === location.href) return;
  sessionStorage.setItem(key, location.href);
  window.location.reload();
});

const queryClient = new QueryClient({
  defaultOptions: { queries: { staleTime: 10_000, retry: 1 } },
});

const router = createRouter({
  routeTree,
  context: { queryClient },
  // Preload route chunks + loaders on hover/focus so most navigations never show a spinner.
  defaultPreload: "intent",
  defaultPreloadStaleTime: 30_000,
  // Child routes fall back to a spinner inside the shell, not the root's full-screen one.
  defaultPendingComponent: PagePending,
  defaultPendingMs: 150,
  defaultPendingMinMs: 200,
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

/**
 * VITE_API_MOCK=true serves every endpoint from the in-browser fixtures (no API or database
 * needed). Anything else talks to the real API. Production builds never load the worker.
 */
async function enableMocks() {
  if (import.meta.env.VITE_API_MOCK !== "true") return;
  const { createWorker } = await import("./mocks/browser");
  await createWorker().start({ onUnhandledRequest: "bypass", quiet: true });
}

const rootEl = document.getElementById("root");
if (!rootEl) throw new Error("#root missing");

enableMocks().then(() => {
  createRoot(rootEl).render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <RouterProvider router={router} />
      </QueryClientProvider>
    </StrictMode>,
  );
});
